<template>
  <div class="quiz">

    <!-- Step 1 Generic Questions, round 1 -->
    <div v-if="step === 1" class="step">
      <h1 v-html="$t('genericQuestions['+primaryQuestion+'].question')" class="question"></h1>

      <div class="answers"
          :class="{hasfive: hasFive($t('genericQuestions['+primaryQuestion+'].answers')),
                  haseight: hasEight($t('genericQuestions['+primaryQuestion+'].answers'))}">
        <div v-for="answer in randomize($t('genericQuestions['+primaryQuestion+'].answers'))"
          @click="selectAnswer({ answer: answer.category, points: 1 }); selectedAnswers.includes(answer.answer) ? selectedAnswers.splice(selectedAnswers.indexOf(answer.answer), 1) : selectedAnswers.push(answer.answer)"
          :key="answer.category"
          :class="{highlight: selectedAnswers.includes(answer.answer)}"
          class="answer-wrapper">

          <div class="answer-checkbox"></div>
          <p v-html="answer.answer" class="answer"></p>
        </div>
      </div>

      <div class="restart-wrapper">
        <a href="/" v-html="$t('message.startOver')" class="restart"></a>
      </div>
    </div>

    <!-- Step 2: Primary Category -->
    <div v-else-if="step === 2" class="step">
      <h1 v-html="$t('category_questions.'+primaryCategory+'[0].question')" class="question"></h1>

      <div class="answers"
          :class="{hasfive: hasFive($t('category_questions.'+primaryCategory+'[0].answers')),
                  haseight: hasEight($t('category_questions.'+primaryCategory+'[0].answers'))}">
        <div v-for="answer in randomize($t('category_questions.'+primaryCategory+'[0].answers'))"
          :key="answer.id"
          :class="{highlight: selectedAnswers.includes(answer.answer)}"
          class="answer-wrapper has-input-checkbox">

          <label>
            <div class="answer-checkbox"></div>

            <input v-model="checkedTraits"
              :value="answer.trait + answer.id"
              @change="answerClicked(answer.answer)"
              type="checkbox"
              class="answer">
            {{answer.answer}}
          </label>
        </div>
      </div>

      <div class="restart-wrapper">
        <a href="/" v-html="$t('message.startOver')" class="restart"></a>
      </div>
    </div>

    <!-- Step 3: Generic Questions, round 2 -->
    <div v-else-if="step === 3" class="step">
      <h1 v-html="$t('genericQuestions['+secondaryQuestion+'].question')" class="question"></h1>

      <div class="answers"
          :class="{hasfive: hasFive($t('genericQuestions['+secondaryQuestion+'].answers'), primaryCategory),
                  haseight: hasEight($t('genericQuestions['+secondaryQuestion+'].answers'))}">
        <template v-for="answer in randomize($t('genericQuestions['+secondaryQuestion+'].answers'))">

          <div v-if="answer.category != primaryCategory"
            @click="selectAnswer({ answer: answer.category, points: 1 }); selectedAnswers.includes(answer.answer) ? selectedAnswers.splice(selectedAnswers.indexOf(answer.answer), 1) : selectedAnswers.push(answer.answer)"
            :key="answer.category"
            :class="{highlight: selectedAnswers.includes(answer.answer)}"
            class="answer-wrapper">

            <div class="answer-checkbox"></div>
            <p v-html="answer.answer" class="answer"></p>
          </div>

        </template>
      </div>

      <div class="restart-wrapper">
        <a href="/" v-html="$t('message.startOver')" class="restart"></a>
      </div>
    </div>

    <!-- Step 4: Secondary Category -->
    <div v-else-if="step === 4" class="step">
      <h1 v-html="$t('category_questions.'+secondaryCategory+'[0].question')" class="question"></h1>

      <div class="answers"
          :class="{hasfive: hasFive($t('category_questions.'+secondaryCategory+'[0].answers')),
                  haseight: hasEight($t('category_questions.'+secondaryCategory+'[0].answers'))}">
        <div v-for="answer in randomize($t('category_questions.'+secondaryCategory+'[0].answers'))"
          :key="answer.id"
          :class="{highlight: selectedAnswers.includes(answer.answer)}"
          class="answer-wrapper has-input-checkbox">

          <label>
            <div class="answer-checkbox"></div>

            <input v-model="checkedTraits"
              :value="answer.trait + answer.id"
              @change="answerClicked(answer.answer)"
              type="checkbox"
              class="answer">
            {{answer.answer}}
          </label>
        </div>
      </div>

      <div class="restart-wrapper">
        <a href="/" v-html="$t('message.startOver')" class="restart"></a>
      </div>
    </div>

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import seedrandom from 'seedrandom'
import shuffle from 'shuffle-array'

export default {
  name: 'begin',
  seed: null,
  data: function () {
    return {
      checkedTraits: [],
      selectedAnswers: []
    }
  },
  watch: {
    checkedTraits: function (traits) {
      if (traits.length === 2) {
        var points = (this.step === 2 ? 5 : 3)
        traits.forEach(trait => {
          // Trim off the id from the trait (example: Bold2)
          this.selectTrait({ trait: trait.slice(0, -1), points: points })
        })
        this.proceed()
        // uncheck traits to not interfere with next time through
        this.checkedTraits = []
      }
    },
    step: function (step) {
      if (step === 5) {
        var person = (this.$route.name === 'quizOther' ? 'o' : 'i')
        this.$router.push({ name: 'results',
          params: {
            person: person,
            trait1: this.traits[0],
            trait2: this.traits[1],
            trait3: this.traits[2],
            trait4: this.traits[3]
          }
        })
      }
    }
  },
  computed: mapState({
    step: state => state.step,
    primaryQuestion: state => state.primaryQuestion,
    primaryCategory: state => state.primaryCategory,
    secondaryQuestion: state => {
      return state.primaryQuestion + state.secondaryQuestion
    },
    secondaryCategory: state => state.secondaryCategory,
    traits: state => {
      // Return the top 4 traits
      return Object.keys(state.traits).sort(function (a, b) {
        return state.traits[b] - state.traits[a]
      }).slice(0, 4)
    }
  }),
  methods: {
    ...mapActions([
      'proceed',
      'selectAnswer',
      'selectTrait'
    ]),
    randomize (array) {
      // Seed the random number generator
      if (!this.seed) {
        this.seed = Math.random()
      }
      // The seed will be stable for each quesiton.
      var stableRNG = seedrandom('' + this.seed + this.step + this.primaryQuestion + this.secondaryQuestion)
      return shuffle(array, { 'copy': true, 'rng': stableRNG })
    },
    answerClicked (answer) {
      this.selectedAnswers.includes(answer) ? this.selectedAnswers.splice(this.selectedAnswers.indexOf(answer), 1) : this.selectedAnswers.push(answer)
    },
    hasFive (answers, primaryCategory) {
      if (answers.length === 5) {
        return true
      }
      if (primaryCategory !== undefined && primaryCategory !== '') {
        let index = answers.findIndex(a => a.category === primaryCategory)
        if (index !== -1) {
          return true
        }
      }
    },
    hasEight (answers) {
      if (answers.length === 8) {
        return true
      }
    }
  }
}
</script>

<style lang="scss" src="../styles/quiz.scss"></style>
